/** Underline tabs (Radix). */
import * as RTabs from '@radix-ui/react-tabs';
import type { ReactNode } from 'react';

import { cn } from './cn.js';

export interface TabItem {
  value: string;
  label: ReactNode;
}

export function Tabs({
  value,
  onValueChange,
  items,
  className,
}: {
  value: string;
  onValueChange: (v: string) => void;
  items: TabItem[];
  className?: string;
}): ReactNode {
  return (
    <RTabs.Root value={value} onValueChange={onValueChange}>
      <RTabs.List
        className={cn('flex items-center gap-1 border-b border-border', className)}
        aria-label="Tabs"
      >
        {items.map((it) => (
          <RTabs.Trigger
            key={it.value}
            value={it.value}
            className={cn(
              'relative -mb-px cursor-pointer px-3 py-2.5 text-sm font-medium text-muted transition-colors',
              'hover:text-fg focus-visible:outline-none',
              'data-[state=active]:text-primary',
              'after:absolute after:inset-x-0 after:-bottom-px after:h-0.5 after:bg-transparent',
              'data-[state=active]:after:bg-primary',
            )}
          >
            {it.label}
          </RTabs.Trigger>
        ))}
      </RTabs.List>
    </RTabs.Root>
  );
}

/** Pill-style segmented control (filters, view switches). */
export function SegmentedControl<T extends string>({
  value,
  onChange,
  options,
  className,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: ReactNode }[];
  className?: string;
}): ReactNode {
  return (
    <div
      className={cn('inline-flex rounded-lg border border-border bg-surface-2 p-0.5', className)}
    >
      {options.map((opt) => (
        <button
          key={opt.value}
          onClick={() => onChange(opt.value)}
          className={cn(
            'cursor-pointer rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
            value === opt.value ? 'bg-surface text-fg shadow-card' : 'text-muted hover:text-fg',
          )}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}
